const REDACTED = '[REDACTED]';

const EXACT_AUTH_FIELDS: ReadonlySet<string> = new Set(['password', 'password_hash', 'mfa_secret', 'totp_secret', 'webauthn_credential', 'refresh_token', 'access_token', 'id_token', 'code']);

const FALLBACK_AUTH_FIELD_PATTERN = /secret|token|password|credential/i;

/**
 * Per spec Section 2.5 — pino redact key paths covering the canonical
 * authentication-sensitive field names. Wired as the pino `redact.paths`
 * array in service-level pino config.
 */
export const PINO_AUTH_REDACT_PATHS: readonly string[] = [
  'password',
  '*.password',
  '*.*.password',
  'password_hash',
  '*.password_hash',
  '*.*.password_hash',
  'mfa_secret',
  '*.mfa_secret',
  '*.*.mfa_secret',
  'totp_secret',
  '*.totp_secret',
  '*.*.totp_secret',
  'webauthn_credential',
  '*.webauthn_credential',
  '*.*.webauthn_credential',
  'refresh_token',
  '*.refresh_token',
  '*.*.refresh_token',
  'access_token',
  '*.access_token',
  '*.*.access_token',
  'id_token',
  '*.id_token',
  '*.*.id_token',
  'code',
  '*.code',
  '*.*.code',
];

function shouldRedact(key: string): boolean {
  const lower = key.toLowerCase();
  if (EXACT_AUTH_FIELDS.has(lower)) return true;
  return FALLBACK_AUTH_FIELD_PATTERN.test(key);
}

/**
 * Recursively redacts authentication-sensitive fields from a payload. Matches
 * any nested key whose name matches the canonical set or the fallback regex
 * (case-insensitive). Non-matching fields pass through unchanged.
 *
 * Used as a Sentry `beforeSend` hook (spec Section 2.5) and as a pino
 * formatter for arbitrary log object payloads.
 */
export function scrubAuthFields(input: unknown): unknown {
  if (input === null || input === undefined) return input;
  if (typeof input !== 'object') return input;

  if (Array.isArray(input)) {
    return input.map(scrubAuthFields);
  }

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (shouldRedact(key)) {
      out[key] = REDACTED;
    } else {
      out[key] = scrubAuthFields(value);
    }
  }
  return out;
}

/**
 * Sentry `beforeSend` hook: scrubs auth-sensitive fields from event extras,
 * tags, contexts, and request data before the event leaves the host.
 */
type SentryEventText = {
  breadcrumbs?: { message?: string }[];
  exception?: { values?: { value?: string }[] };
  message?: string;
};

export function sentryAuthBeforeSend<T extends { extra?: unknown; contexts?: unknown; request?: unknown; tags?: unknown } & SentryEventText>(event: T): T {
  if (event.extra) event.extra = scrubAuthFields(event.extra) as typeof event.extra;
  if (event.contexts) event.contexts = scrubAuthFields(event.contexts) as typeof event.contexts;
  if (event.request) event.request = scrubAuthFields(event.request) as typeof event.request;
  if (event.tags) event.tags = scrubAuthFields(event.tags) as typeof event.tags;
  if (event.message !== undefined) event.message = maskEmails(event.message) as typeof event.message;
  for (const value of event.exception?.values ?? []) {
    if (value.value !== undefined) value.value = maskEmails(value.value);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.message !== undefined) breadcrumb.message = maskEmails(breadcrumb.message);
  }
  return event;
}

const MASKED_EMAIL = '[email]';

const isAsciiAlphanumeric = (character: string): boolean => /^[A-Za-z0-9]$/.test(character);

const isLocalPartCharacter = (character: string): boolean => isAsciiAlphanumeric(character) || '._%+-'.includes(character);

const isDomainCharacter = (character: string): boolean => isAsciiAlphanumeric(character) || character === '.' || character === '-';

export function maskEmails(text: string): string {
  let out = '';
  let copiedUntil = 0;
  let index = 0;
  while (index < text.length) {
    if (text[index] === '@') {
      let start = index;
      while (start > 0 && isLocalPartCharacter(text[start - 1])) start -= 1;
      let end = index + 1;
      while (end < text.length && isDomainCharacter(text[end])) end += 1;
      while (end > index + 1 && (text[end - 1] === '.' || text[end - 1] === '-')) end -= 1;
      const domain = text.slice(index + 1, end);
      if (start < index && start >= copiedUntil && isAsciiAlphanumeric(domain.charAt(0)) && domain.includes('.')) {
        out += text.slice(copiedUntil, start) + MASKED_EMAIL;
        copiedUntil = end;
        index = end;
        continue;
      }
    }
    index += 1;
  }
  return out + text.slice(copiedUntil);
}
